<template>
  <span v-if="task">
    <div class="d-flex align-items-center mb-3">
      <div class="num mr-2">#{{ task.no }}</div>
      <div class="title mr-3">{{ task.name | translate }}</div>
    </div>
    <div class="task-flex">
      <div>
        <div v-if="task">
          <button class="link-button mb-3" v-b-toggle.collapse-file-rocket><i class="iconoir-attachment"></i> {{ $t("file-manager.attach") }}</button>
          <b-collapse id="collapse-file-rocket" class="mt-2">
            <b-tabs class="nav-tabs-custom">
              <b-tab>
                <template v-slot:title>
                  <h6 class="m-1">{{ $t("file-manager.select-from-list") }}</h6>
                </template>
                <div class="ml-2 mt-4 mb-4">
                  <filter-select
                    class="w-75 d-inline-block"
                    id="file-filter"
                    ref="no-filter"
                    :placeholder="$t('file-manager.select-from-list')"
                    :multiple="false"
                    @filter="onFileSearch"
                    :selected="selectedFile"
                    @changed="selectFile"
                  />

                  <sweet-button @click="attachFile()" :disabled="!this.selectedFile" type="submit" variant="success" icon="mdi mdi-check" size="sm" buttonClass="ml-3">
                    {{ $t("common.save") }}
                  </sweet-button>
                </div>
              </b-tab>
              <b-tab>
                <template v-slot:title>
                  <h6 class="m-1">{{ $t("file-manager.upload-from-disk") }}</h6>
                </template>

                <div class="ml-2 mt-4 mb-4">
                  <file-rocket
                    :multiple="true"
                    :allowedFormats="allowedFormats"
                    @response="handleUploadResponse"
                    :showProgress="'Upload'"
                    :maxFileSize="600000000000"
                    :maxFileLength="1"
                    :targetUrl="targetUrl"
                    :payload="selectedFileIdInObject"
                  >
                  </file-rocket>
                </div>
              </b-tab>
            </b-tabs>
          </b-collapse>
        </div>

        <div class="mb-3 font-size-14 text-muted">{{ task.description | translate }}</div>

        <div v-if="task.status == 'DONE'" class="mb-3">
          <h6 class="title mt-3 text-muted">{{ $t("task.done-reason") }}</h6>
          {{ task.doneReason | translate }}
        </div>

        <div class="d-flex align-items-center mb-4">
          <div v-if="task.taskRelations.length > 0" class="tab2 mr-3" :class="{ active: modalTab === 'taskRelations' }" @click="modalTab = 'taskRelations'">
            {{ $t("task.relations") }} ({{ task.taskRelations.length }})
          </div>
          <div class="tab2 mr-3" :class="{ active: modalTab === 'comments' }" @click="modalTab = 'comments'">
            {{ $t("common.comment.plural") }}
            <span v-if="!isCommentsLoading && commentPagination">({{ commentPagination.totalDocs }})</span>
          </div>
        </div>

        <div v-if="modalTab === 'taskRelations'">
          <div v-for="link in task.taskRelations" :key="'related' + link.type + task._id + link.task._id" @click="reloadwith(link.task)" class="hand">
            <task-item :task="link.task" :linkType="link.type" :relatedTask="task" />
          </div>
        </div>

        <div v-if="modalTab === 'comments'">
          <div class="d-flex mb-4" style="gap: .6rem;">
            <task-avatar :account="currentAccount" size="xs" />
            <textarea v-model="comment" class="comment-textarea" :placeholder="$t('common.comment.text')"></textarea>
            <b-button @click="createComment">{{ $t("common.create") }}</b-button>
          </div>
          <content-loading v-if="isCommentsLoading" />
          <div v-else-if="commentPagination && commentPagination.docs">
            <div v-for="comment in commentPagination.docs" :key="comment._id" class="d-flex mb-4">
              <div class="userImage">
                <task-avatar :account="comment.account" />
              </div>
              <div class="event">
                <div>
                  <b>{{ comment.account.name }}</b>
                  <span class="date ml-3">{{ comment.createdAt | date(true) }}</span>
                  <i v-if="comment.account._id == currentAccount._id" @click="deleteComment(comment)" class="bx bx-trash text-danger float-right hand"></i>
                </div>
                <div class="d-flex align-items-center mt-2">
                  {{ comment.name | translate }}
                </div>
              </div>
            </div>

            <div v-if="commentPagination && commentPagination.totalPages > 1" class="row">
              <div class="col">
                <ul class="pagination pagination-rounded mb-0">
                  <b-pagination
                    v-model="commentRequest.options.page"
                    :total-rows="commentPagination.totalDocs"
                    :per-page="commentPagination.limit"
                    limit="10"
                    size="sm"
                  ></b-pagination>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div v-if="task.files.length > 0">
          <div class="d-flex align-items-center">
            <div class="tab2 mt-4 " :class="{ active: true }">
              {{ $t("file-manager.files") }}
              <span>({{ task.files.length }})</span>
            </div>
          </div>
          <div class="row">
            <div class="col-md-3 d-flex" v-for="file in paginatedFileData" :key="file._id">
              <div class="card shadow-none">
                <div class="card-body text-center d-flex align-items-center flex-column">
                  <div v-b-tooltip.hover :title="$options.filters.translate(file.name)" class="mb-2">
                    <file-icon class="hand" :extension="file.extension" />
                    <!--  <i :class="getIconClass(file.extension)" style="font-size:2rem;"></i>-->
                    <p class="card-text">{{ file.name | translate | summary(15) }}</p>
                  </div>
                  <div class="mt-auto">
                    <i @click="downloadFile(file)" v-b-tooltip.hover :title="$t('file-manager.download')" class="bx bxs-download text-dark hand mr-2" style="font-size:1.2rem;"></i>
                    <i @click="deleteFile(file)" v-b-tooltip.hover :title="$t('common.delete')" class="bx bx-trash text-danger hand" style="font-size:1.2rem;"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <b-pagination
            class="mt-2"
            v-if="task.files.length > filePagination.perPage"
            v-model="filePagination.currentPage"
            :total-rows="task.files.length"
            :per-page="filePagination.perPage"
            limit="5"
            size="sm"
          ></b-pagination>
        </div>
      </div>

      <div>
        <div class="title mb-3">
          {{ $t("common.detail") }}
        </div>

        <div class="d-flex align-items-center mb-3">
          <div class="bold-text text-muted" style="width: 170px;">{{ $t("task.status.text") }}:</div>
          <task-status :status="task.status" />
        </div>

        <div class="d-flex align-items-center mb-3">
          <div class="bold-text text-muted" style="width: 170px;">{{ $t("organization.text") }}:</div>
          <!-- <letter-avatar :name="task.organization.name" :showName="true" size="xxs" :image="task.organization.logo" variant="secondary" /> -->
          {{ task.organization.name }}
        </div>

        <div class="d-flex align-items-center mb-3">
          <div class="bold-text text-muted" style="width: 170px;">{{ $t("task.assignee") }}:</div>
          <div v-if="task.assignee" class="d-flex align-items-center">
            <div class="userImage">
              <task-avatar :account="task.assignee" size="xs" />
            </div>
            <div class="userName">{{ task.assignee.name }}</div>
          </div>
          <div v-else class="d-flex align-items-center">
            <task-avatar :account="task.assignee" size="xs" />
            <div class="userName">{{ $t("task.unassigned") }}</div>
          </div>
        </div>

        <div class="d-flex align-items-center mb-3">
          <div class="bold-text text-muted" style="width: 170px;">{{ $t("task.reporter") }}:</div>
          <div v-if="task.reporter" class="d-flex align-items-center">
            <div class="userImage">
              <task-avatar :account="task.reporter" size="xs" />
            </div>
            <div class="userName">{{ task.reporter.name }}</div>
          </div>
          <div v-else class="d-flex align-items-center">
            <task-avatar :account="task.reporter" size="xs" way="reporter" />
            <div class="userName">{{ $t("task.system-reporter") }}</div>
          </div>
        </div>

        <div class="d-flex align-items-center mb-3">
          <div class="bold-text text-muted" style="width: 170px;">{{ $t("task.priority.text") }}:</div>
          <task-priority :priority="task.priority" size="13" />
        </div>

        <div class="d-flex align-items-center mb-3">
          <div class="bold-text text-muted" style="width: 170px;">{{ $t("task.duedate") }}:</div>
          <div class="bold-text">{{ task.duedate | date }}</div>
        </div>

        <div v-if="task.status == 'DONE'" class="d-flex align-items-center mb-3">
          <div class="bold-text text-muted" style="width: 170px;">{{ $t("task.donedate") }}:</div>
          <div class="bold-text">{{ task.donedate | date }}</div>
        </div>

        <div class="d-flex align-items-center mb-3" v-if="task.category">
          <div class="bold-text text-muted" style="width: 170px;">{{ $t("task.category") }}:</div>
          <div class="bold-text">{{ task.category.name | translate }}</div>
        </div>

        <div class="d-flex align-items-center mb-3" v-if="hasSource">
          <div class="bold-text text-muted" style="width: 170px;">{{ $t("task.source-generic") }}:</div>
          <div class="bold-text">
            <task-source :caused="task.caused" />
          </div>
        </div>

        <div v-if="task.products.length" class="d-flex align-items-center mb-5">
          <div class="bold-text text-muted" style="width: 170px;">{{ $t("product.text") }}:</div>
          <div class="bold-text">{{ task.products.join(", ") }}</div>
        </div>

        <div
          v-if="task.relations.policies.length || task.relations.risks.length || task.relations.roadmapStage || task.relations.application || task.relations.question"
          class="title mb-2"
        >
          {{ $t("common.associate-assets") }}
        </div>

        <div v-for="policy in task.relations.policies" :key="policy._id">
          <div class="asset-link" @click="openPolicyDetailModal(policy)">
            <policy-status :acceptance="policy.acceptance" />

            {{ policy.name | translate }}
            <i class="iconoir-arrow-down-right"></i>
          </div>
        </div>

        <div v-for="risk in task.relations.risks" :key="risk._id">
          <div class="asset-link hand" @click="goRisk(risk)">
            <i class="iconoir-activity orange" style="font-size: 1.25rem; "></i> {{ risk.name | translate }}
            <i class="iconoir-arrow-down-right"></i>
          </div>
        </div>
        <div v-if="task.relations.application">
          <div class="asset-link hand" @click="goApplication(task.relations.application)">
            <i class="iconoir-bookmark-book orange" style="font-size: 1.25rem; "></i> {{ task.relations.application.applicationSubject }}
            <i class="iconoir-arrow-down-right"></i>
          </div>
        </div>
        <div v-if="task.relations.question">
          <div class="asset-link hand" @click="goQuestionSet(task.relations.question.question.questionSet)">
            <i class="iconoir-chat-bubble-question orange" style="font-size: 1.25rem; "></i> {{ task.relations.question.question.name | translate }}
            <i class="iconoir-arrow-down-right"></i>
          </div>
        </div>

        <div v-if="task.relations && task.relations.roadmapStage" class="asset-link hand" style="align-items: flex-start;" @click="goRoadmapPage(task.relations.roadmapStage)">
          <img v-if="task.relations.roadmapStage.roadmapDefinition == 'KVKK_PRODUCT_ROADMAP'" src="/icons/kvkk-active.svg" style="margin-top: 4px; width: 1.10531rem;" alt="" />
          <img v-if="task.relations.roadmapStage.roadmapDefinition == 'GDPR_PRODUCT_ROADMAP'" src="/icons/gdpr-active.svg" style="margin-top: 4px; width: 1.10531rem;" alt="" />
          <i v-if="task.relations.roadmapStage.roadmapDefinition == 'PLATFORM_ROADMAP'" class="iconoir-cube-hole" style="margin-top: 4px; width: 1.10531rem;"></i>

          <div>
            <span v-if="task.relations.roadmapStage.roadmapDefinition == 'KVKK_PRODUCT_ROADMAP'">{{ $t("roadmap.kvkk.text") }}</span>
            <span v-if="task.relations.roadmapStage.roadmapDefinition == 'GDPR_PRODUCT_ROADMAP'">{{ $t("roadmap.gdpr.text") }}</span>
            <span v-if="task.relations.roadmapStage.roadmapDefinition == 'PLATFORM_ROADMAP'">{{ $t("roadmap.platform.text") }}</span>
            <span v-if="task.relations.roadmapStage.roadmapDefinition == 'BGYS_ROADMAP'">{{ $t("roadmap.bgys.text") }}</span>

            <br />
            <b class="text-gray" style="font-size: 1rem;">
              {{ stringifyRoadmapStageDefinition(task.relations.roadmapStage.definition) }}
            </b>
          </div>
          <i class="iconoir-arrow-down-right"></i>
        </div>
      </div>
    </div>
  </span>
</template>

<script>
import FileRocket from "@c/form/file-rocket";
import FileIcon from "@modules/file-manager/file-icon.vue";
import PolicyStatus from "../../../modules/policy/component/policy-status";

export default {
  components: { PolicyStatus, FileRocket, FileIcon },
  data() {
    return {
      modalTab: "taskRelations",
      comment: undefined,
      commentRequest: {
        options: { page: 1, limit: 10, sort: {} },
        query: {},
      },
      filePagination: {
        currentPage: 1,
        perPage: 4,
      },
      selectedFile: undefined,
      allowedFormats: ["application/*", "image/*", "text/*"],
    };
  },
  computed: {
    selectedFileIdInObject() {
      return this.selectedFile ? { selectedFile: this.selectedFile._id } : { selectedFile: undefined };
    },
    task() {
      return this.$store.getters["task/task"];
    },
    isCommentsLoading() {
      return this.$store.getters["task/isCommentsLoading"];
    },
    isUploading() {
      return this.$store.getters["fileManager/isUploading"];
    },
    commentPagination() {
      return this.$store.getters["task/commentPagination"];
    },
    targetUrl() {
      return `task/${this.task._id}/upload`;
    },
    paginatedFileData() {
      const start = (this.filePagination.currentPage - 1) * this.filePagination.perPage;
      const end = start + this.filePagination.perPage;
      return this.task.files.slice(start, end);
    },
    hasSource() {
      const entity = this.task?.caused?.entity;
      if (!entity) return false;
      return Object.keys(entity).some((k) => entity[k] && entity[k]._id);
    },
  },
  watch: {
    task: {
      deep: true,
      immediate: true,
      handler(v) {
        if (!v) return;

        if (this.task.taskRelations.length > 0) {
          this.modalTab = "taskRelations";
        } else {
          this.modalTab = "comments";
        }
      },
    },

    commentRequest: {
      deep: true,
      immediate: true,

      handler() {
        if (!this.task?._id) return;
        this.paginateComments();
      },
    },
  },
  methods: {
    stringifyRoadmapStageDefinition(definition) {
      const key = definition.toLowerCase().replace(/_/g, "-");
      return this.$t(`roadmap.stage.${key}`);
    },
    async attachFile() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch("task/attachFile", { fileId: this.selectedFile._id, taskId: this.task._id });
        this.$toast.success(this.$t("common.success"));
        this.reloadwith(this.task);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.selectedFile = undefined;
      }
    },
    async onFileSearch(value, callback) {
      const categories = await this.$store.dispatch(`organizationFileCategory/paginateParentCategories`, {
        options: { limit: 200 },
        query: { categoryDefinition: { value: "TASK", isObject: true }, ...(this.scope ? { products: { value: this.scope, isObject: true } } : {}) },
      });

      const category = categories.docs?.[0];

      const response = await this.$store.dispatch(`fileManager/paginateSimple`, {
        options: { limit: 200 },
        query: {
          name: { value },
          ...(category ? { category: { value: { _id: category._id }, isObject: true } } : {}),
          ...(this.scope ? { products: { value: this.scope, isObject: true } } : {}),
        },
      });

      callback(response.data?.docs ?? []);
    },
    selectFile(item) {
      this.selectedFile = item;
    },

    reloadwith(task) {
      this.$emit("reloadwith", task);
    },
    paginateComments() {
      this.$store.dispatch("task/paginateComments", { pagination: this.commentRequest, taskId: this.task._id });
    },
    async deleteComment(comment) {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch(`task/deleteComment`, comment._id);
        this.paginateComments();
        this.$toast.success(this.$t("common.success"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    async createComment() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }
      try {
        const lang = this.currentLanguage;

        await this.$store.dispatch(`task/createComment`, {
          [`name.${lang}`]: this.comment,
          task: { _id: this.task._id },
        });
        this.comment = undefined;
        this.paginateComments();
        this.$toast.success(this.$t("common.success"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    onStatusChanged(status) {
      this.$emit("onStatusChanged", status);
    },
    openPolicyDetailModal(policy) {
      this.$store.commit("organizationPolicy/policyId", policy._id);
      this.$store.commit("task/showTaskDetailModal", false);
      this.$bvModal.show("policy-detail-modal");
    },
    downloadFile(file) {
      this.$store.commit("fileManager/addDownloadedFile", { file });
    },
    async deleteFile(item) {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch("fileManager/delete", item._id);
        this.reloadwith(this.task);

        this.$toast.success(`${this.$t("common.delete-successful")} [${this.$options.filters.translate(item.name)}]`);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error, { customeMessage: `${this.$t("common.delete-fail")} [${this.$options.filters.translate(item.name)}]` });
      }
    },
    handleUploadResponse(event) {
      if (!this.isUploading) {
        if (event && event?.status !== 200) {
          this.$handleHttpErrorByStatusCode(event?.error);
        }
        setTimeout(() => {
          this.reloadwith(this.task);
        }, 1000);
      }
    },

    goRoadmapPage(stage) {
      let path;
      if (stage.roadmapDefinition == "GDPR_PRODUCT_ROADMAP") {
        path = "/gdpr/roadmap";
      } else if (stage.roadmapDefinition == "KVKK_PRODUCT_ROADMAP") {
        path = "/kvkk/roadmap";
      } else if (stage.roadmapDefinition == "BGYS_PRODUCT_ROADMAP") {
        path = "/bgys/roadmap";
      } else if (stage.roadmapDefinition == "DDO_PRODUCT_ROADMAP") {
        path = "/ddo/roadmap";
      } else if (stage.roadmapDefinition == "PLATFORM_ROADMAP") {
        path = "/organization/roadmap";
      }
      this.selectOrganizationAndGo(stage.organization, path, true);
    },
    goQuestionSet(questionSet) {
      const path = `question-set/answering/${questionSet._id}`;
      this.selectOrganizationAndGo(questionSet.organization, path, true);
    },
    goApplication(application) {
      const path = `/organization/application/${application._id}`;
      this.selectOrganizationAndGo(application.organization._id, path, true);
    },
    goRisk(risk) {
      const product = risk.products.includes(this.currentProduct) ? this.currentProduct : risk.products[0];
      const path = product == "RISK" ? `/risk/corporate-risk/detail/${risk._id}` : `/${product?.toLowerCase()}/risk-detail/${risk._id}`;
      this.selectOrganizationAndGo(risk.organization, path, true);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../modules/task/tasks.scss";
</style>
